import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { KeyRound, Tag, ClipboardList } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { useAuth } from '../../hooks/useAuth';
import { PlanMaster, LicenseCategoryMaster, LicenseTypeMaster } from './MasterPages';

type TabKey = 'plans' | 'license-categories' | 'license-types';

const TABS: { key: TabKey; label: string; icon: React.ElementType; permission: string; Component: React.FC }[] = [
    { key: 'plans', label: 'Plans', icon: KeyRound, permission: 'licenses.manage', Component: PlanMaster },
    { key: 'license-categories', label: 'License Categories', icon: Tag, permission: 'licenses.manage', Component: LicenseCategoryMaster },
    { key: 'license-types', label: 'License Types', icon: ClipboardList, permission: 'licenses.manage', Component: LicenseTypeMaster },
];

const LicenseSetupPage: React.FC = () => {
    const { hasPermission } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();

    const visibleTabs = useMemo(() => TABS.filter((t) => hasPermission(t.permission)), [hasPermission]);

    const requestedTab = searchParams.get('tab') as TabKey | null;
    const activeTab = visibleTabs.some((t) => t.key === requestedTab) ? (requestedTab as TabKey) : visibleTabs[0]?.key;

    const handleTabChange = (value: string) => {
        setSearchParams({ tab: value });
    };

    if (!activeTab) {
        return null;
    }

    return (
        <div className="space-y-4 md:p-6">
            <div>
                <h1 className="text-2xl font-bold tracking-tight">License Setup</h1>
                <p className="text-sm text-muted-foreground">Manage license plans, categories, and licensing models</p>
            </div>
            <Tabs value={activeTab} onValueChange={handleTabChange}>
                <TabsList className="flex h-auto w-full flex-wrap justify-start sm:w-auto">
                    {visibleTabs.map((tab) => (
                        <TabsTrigger key={tab.key} value={tab.key} className="gap-2">
                            <tab.icon className="h-4 w-4" />
                            {tab.label}
                        </TabsTrigger>
                    ))}
                </TabsList>
                {visibleTabs.map((tab) => (
                    <TabsContent key={tab.key} value={tab.key}>
                        <tab.Component />
                    </TabsContent>
                ))}
            </Tabs>
        </div>
    );
};

export default LicenseSetupPage;
